import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { PlanKey } from '../api/client'
import { fmtNum, usePricing } from '../hooks/usePricing'

// Стоимость в оферте — это цена, по которой реально списывают деньги, поэтому
// она берётся из того же источника (backend/app/tariffs.py), а не набирается
// текстом. Расхождение оферты с суммой списания — не опечатка, а нарушение.
const TIER_TERMS: { name: string; monthKey: PlanKey; yearKey: PlanKey }[] = [
  { name: 'Plus', monthKey: 'plus', yearKey: 'plus_yearly' },
  { name: 'Pro',  monthKey: 'pro',  yearKey: 'pro_yearly'  },
]

/** Публичная оферта Delo. Официальный документ — обращение на «Вы». */
export function Offer() {
  const pricing = usePricing()

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <Link
          to="/profile"
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> Профиль
        </Link>
        <p className="text-h1 text-ink">Публичная оферта</p>
        <p className="text-sm text-ink-2 mt-0.5">Версия от 22.07.2026</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-4">
        <Section title="1. Общие положения">
          Настоящий документ является публичной офертой самозанятого Захарова Семёна Алексеевича,
          ИНН 771874983254 (далее — Исполнитель), и содержит предложение заключить договор
          возмездного оказания услуг на изложенных ниже условиях.
          {' '}
          В соответствии со статьёй 437 Гражданского кодекса Российской Федерации настоящий
          документ является публичной офертой. Акцептом оферты в соответствии с пунктом 3
          статьи 438 Гражданского кодекса Российской Федерации признаётся оплата Заказчиком
          подписки на Сервис.
        </Section>

        <Section title="2. Предмет договора">
          Исполнитель обязуется предоставить Заказчику доступ к расширенной функциональности
          сервиса Delo в соответствии с выбранным тарифным планом, а Заказчик обязуется оплатить
          указанный доступ.
          {' '}
          Перечень функциональности, включённой в каждый тарифный план, размещается в Сервисе
          по адресу deloapp.online/subscription и является неотъемлемой частью настоящей оферты.
        </Section>

        <Section title="3. Тарифные планы и стоимость">
          <List items={TIER_TERMS.map(t => {
            const month = pricing.plan(t.monthKey)?.rub
            const year = pricing.plan(t.yearKey)?.rub
            if (month === undefined || year === undefined) return `Тариф ${t.name} — стоимость уточняется.`
            return `Тариф ${t.name} — ${fmtNum(month)} рублей за календарный месяц ` +
              `либо ${fmtNum(year)} рублей за календарный год.`
          })} />
          Стоимость указана в российских рублях и включает все применимые налоги. Исполнитель
          применяет специальный налоговый режим «Налог на профессиональный доход», в связи с чем
          налог на добавленную стоимость не начисляется.
          {' '}
          Исполнитель вправе изменять стоимость тарифных планов. Изменение стоимости не влияет
          на оплаченный период.
        </Section>

        <Section title="4. Порядок оплаты">
          Оплата производится единовременным платежом за выбранный период посредством платёжной
          системы Telegram Stars либо банковской картой через платёжный сервис ЮMoney.
          {' '}
          Автоматическое продление подписки не осуществляется. По истечении оплаченного периода
          доступ к расширенной функциональности прекращается.
          {' '}
          При оплате нового периода до истечения текущего оплаченный срок суммируется с оставшимся.
          {' '}
          Фискальный чек направляется Заказчику способом, предусмотренным платёжным сервисом.
        </Section>

        <Section title="5. Момент оказания услуги">
          Услуга считается оказанной с момента предоставления Заказчику доступа к расширенной
          функциональности, что происходит незамедлительно после подтверждения оплаты.
        </Section>

        <Section title="6. Возврат денежных средств">
          Заказчик вправе отказаться от услуги и потребовать возврата уплаченных денежных средств
          в течение четырнадцати календарных дней с момента оплаты при условии, что расширенная
          функциональность не использовалась. Под использованием понимается направление сообщений
          Сервису, подключение сторонних сервисов либо иное обращение к функциональности, доступной
          исключительно в рамках оплаченного тарифа, после момента оплаты.
          {' '}
          Заявление о возврате направляется по адресу my@zakharovsem.ru с указанием идентификатора
          учётной записи и даты платежа.
          {' '}
          Заявление рассматривается в течение десяти календарных дней с момента получения.
          При удовлетворении заявления денежные средства возвращаются тем же способом, которым была
          произведена оплата, в срок, не превышающий десяти календарных дней с момента принятия
          решения.
          {' '}
          Возврат денежных средств за подписки, оплаченные посредством Telegram Stars,
          осуществляется в соответствии с правилами платёжной системы Telegram.
          {' '}
          В случае прекращения доступа к Сервису вследствие нарушения Заказчиком Условий
          использования уплаченные денежные средства возврату не подлежат.
        </Section>

        <Section title="7. Реферальная программа">
          Исполнитель предоставляет Заказчику возможность участия в реферальной программе
          на условиях, размещённых в Сервисе.
          {' '}
          Вознаграждение начисляется при оплате подписки приглашённым пользователем и составляет
          тридцать процентов от суммы первого платежа и двадцать процентов от суммы платежей
          со второго по шестой месяц.
          {' '}
          Выплата накопленного вознаграждения производится по заявлению Заказчика при достижении
          минимальной суммы, установленной в Сервисе. Заказчик самостоятельно несёт обязанность
          по уплате налогов с полученного вознаграждения.
          {' '}
          Исполнитель вправе отказать в выплате вознаграждения при выявлении признаков
          недобросовестного использования реферальной программы, включая создание фиктивных
          учётных записей.
        </Section>

        <Section title="8. Ответственность Сторон">
          Ответственность Исполнителя ограничивается стоимостью оплаченной подписки за период,
          в котором возникло основание для ответственности.
          {' '}
          Исполнитель не несёт ответственности за невозможность использования Сервиса вследствие
          обстоятельств, находящихся вне его разумного контроля, включая сбои в работе мессенджера
          Telegram, ограничения доступа со стороны операторов связи, действия государственных
          органов.
        </Section>

        <Section title="9. Срок действия и изменение оферты">
          Настоящая оферта действует с момента размещения и до момента её отзыва Исполнителем.
          {' '}
          Исполнитель вправе изменить условия оферты. Изменения не распространяются на договоры,
          заключённые до момента их размещения.
          {' '}
          Актуальная редакция размещается по адресу deloapp.online/offer.
        </Section>

        <Section title="10. Реквизиты Исполнителя">
          <List items={[
            'Захаров Семён Алексеевич',
            'ИНН 771874983254',
            'Самозанятый, применяющий специальный налоговый режим «Налог на профессиональный доход»',
            'Электронная почта: my@zakharovsem.ru',
          ]} />
        </Section>
      </div>
    </div>
  )
}


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-1 border border-line-1 rounded-lg p-4">
      <p className="text-base font-medium text-ink mb-2">{title}</p>
      <div className="text-sm text-ink-1 leading-relaxed space-y-2">{children}</div>
    </div>
  )
}


function List({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-2 w-1 h-1 rounded-full bg-line-2 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  )
}
